import { z } from 'zod';
import { take } from 'rxjs/operators';
import { ChangeDetectionStrategy, Component, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, FormControl } from '@angular/forms';
import { TranslateService } from "@ngx-translate/core";
import { DialogService } from "primeng/dynamicdialog";
import * as FlatModels from '@medsurf/flat-models';
import * as FlatFacades from '@medsurf/flat-facades';
import { ModalControlComponent } from "@medsurf/flat-components";
import { ZodSharedValidator } from "@medsurf/flat-validators";

@Component({
    selector: 'medsurf-slide-edit-image-annotation-form',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './image-annotation-form.component.html',
    styleUrls: ['./image-annotation-form.component.scss'],
    standalone: false
})
export class ImageAnnotationFormComponent {
  #isSubmitted = signal(false);
  image: FlatModels.ImageEntityModels.Image | null;
  imageAnnotationOptionArray: {value: string, name: any}[];
  imageAnnotationForm = new FormGroup(
    {
      id: new FormControl('')
    },
    {
      validators: ZodSharedValidator.validate(
        z.object({
          id: z.union([z.string().uuid().nullable(), z.literal('all')])
        })
      ),
      updateOn: 'change'
    }
  );
  readonly FlatModels = FlatModels;

  constructor(public alertControlFacade: FlatFacades.AlertControlFacade,
              public slideAuthorFacade: FlatFacades.SlideAuthorFacade,
              public dialogService: DialogService,
              public translate: TranslateService) {
    // Update Image
    this.slideAuthorFacade.currentSelectedSlideImage$.pipe(takeUntilDestroyed()).subscribe((image) => {
      this.image = structuredClone(image);
      if (this.image) {
        this.slideAuthorFacade.imageAnnotationOptionArray$(this.image, this.translate).pipe(take(1)).subscribe((imageAnnotationOptionArray) => {
          this.imageAnnotationOptionArray = imageAnnotationOptionArray;
        });
      } else {
        this.imageAnnotationOptionArray = [];
      }
    });
  }
  
  onSubmit() {
    if (this.#isSubmitted() || this.imageAnnotationForm.invalid || !this.imageAnnotationForm.value.id || !this.image) {
      return;
    }
    this.#isSubmitted.set(true);
    
    if (this.imageAnnotationForm.value.id === 'all') {
      const annotations = this.imageAnnotationOptionArray.reduce((arr, option) => {
        if (option.value !== 'all') {
          arr.push(option.value);
        }
        return arr;
      }, [] as string[]);
      this.slideAuthorFacade.requestAddAnnotationIdsToImage(this.image, annotations, 'history.annotations_add_to_image').subscribe({
        error: (result) => {
          ZodSharedValidator.handleError(result, this.alertControlFacade, this.imageAnnotationForm);
          this.#isSubmitted.set(false);
          this.imageAnnotationForm.reset();
        },
        next: () => {
          this.#isSubmitted.set(false);
          this.imageAnnotationForm.reset();
        }
      });
    } else {
      // Request Add Annotation To Image
      this.slideAuthorFacade.requestAddAnnotationIdToImage(this.image, this.imageAnnotationForm.value.id, 'history.annotation_add_to_image').subscribe({
        error: (result) => {
          ZodSharedValidator.handleError(result, this.alertControlFacade, this.imageAnnotationForm);
          this.#isSubmitted.set(false);
          this.imageAnnotationForm.reset();
        },
        next: () => {
          this.#isSubmitted.set(false);
          this.imageAnnotationForm.reset();
        }
      });
    }
  }
  
  trackByAnnotation(index: number,
                    item: FlatModels.AnnotationEntityModels.AnnotationEntityType) {
    return item.id || index;
  }
  
  goToAnnotation(annotation: FlatModels.AnnotationEntityModels.AnnotationEntityType) {
    this.slideAuthorFacade.requestGoToAnnotation(annotation).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
  }
  
  removeAnnotationFromImage(annotation: FlatModels.AnnotationEntityModels.AnnotationEntityType) {
    if (!this.image) return;
    const dialogRef = this.dialogService.open(ModalControlComponent, {
      header: this.translate.instant('global.annotation_image_remove'),
      data: {
        label: 'global.annotation_image_remove_confirm',
        inputLabel: 'global.remove',
        inputClass: FlatModels.ButtonDesignModels.ButtonDesignClass.DANGER
      },
      dismissableMask: true,
      width: '50%'
    });
    dialogRef.onClose.subscribe(remove => {
      if (this.image && remove) {
        // Request Remove Annotation From image
        this.slideAuthorFacade.requestRemoveAnnotationFromImage(this.image, annotation.id, 'history.annotation_remove_from_image').subscribe({
          error: (result) => this.alertControlFacade.requestAddError(result),
        });
      }
    });
  }
}
